import { useState } from 'react';

import { Card, IconButton } from '@opositandopn/ui';
import classnames from 'classnames';
import { IoArrowForwardCircleOutline } from 'react-icons/io5';

import { ResourceDialog } from '../../../components/shared/resource-dialog/ResourceDialog';
import { useIsPremium, useProfile } from '../../../context/ProfileContext';
import { useAppActions } from '../../../state/appStore';
import { BaseComponentProps } from '../../../types/base-component.types';
import { AssessmentPreviewModel } from '../../assessments/api/useMultipleChoiceAssessments';
import { AssessmentMark } from '../../assessments/components/assessment-mark/AssessmentMark';
import { AssessmentScoreboard } from '../../assessments/components/assessment-scoreboard/AssessmentScoreboard';
import { AssessmentSummary } from '../../assessments/components/assessment-summary/AssessmentSummary';
import { MultipleChoiceAssessmentAttemptDialog } from '../../assessments/components/multiple-choice-assessment-attempt-dialog/MultipleChoiceAssessmentAttemptDialog';
import { MultipleChoiceAssessmentPreview } from '../../assessments/components/multiple-choice-assessment-preview/MultipleChoiceAssessmentPreview';
import { useLatestAssessment } from '../../assessments/useLatestAssessment';

import styles from './FormativeQuestionnaireCard.module.scss';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  /** The subject as the card's heading reads it, in lower case: "teoría". */
  subject: string;
  /**
   * The artwork this subject's card rises out of its corner. One per card: the
   * weekly cards sit side by side, and the same officers twice in a row read as
   * one card rendered twice rather than two subjects.
   */
  image: string;
};

function FormativeQuestionnaireCard({
  className,
  subjectId,
  subject,
  image,
  testId = 'ui-formative-questionnaire-card',
}: ContainerProps) {
  const isPremium = useIsPremium();
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();

  const [previewAssessmentId, setPreviewAssessmentId] = useState<string>();
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string>();

  const {
    assessment: currentAssessment,
    metrics: metricsResponse,
    ownGrade,
    isLoading,
    isMetricsLoading,
  } = useLatestAssessment(subjectId, {
    perspectives: ['instructor'],
    statuses: ['published'],
    purposes: ['formative'],
    referenceStandards: ['criterion-referenced'],
    methodologies: ['quantitative'],
    settings: ['formal'],
    official: false,
    released: true,
  });

  // A subject with no published simulacro yet gets no card at all, rather than an
  // empty box on the home screen.
  if (!isLoading && currentAssessment === undefined) {
    return null;
  }

  const handleStart = (assessment: AssessmentPreviewModel<string>) => {
    // Private simulacros are premium content — same rule as the simulacro list.
    if (assessment.visibility === 'private' && !isPremium) {
      setShowGetPremiumPopup(true);
      return;
    }

    // The button opens the simulacro's preview, as the list does; sitting it is the
    // arrow inside that preview.
    setPreviewAssessmentId(assessment.id);
  };

  return (
    <Card
      className={classnames(styles['card'], className)}
      loading={isLoading || isMetricsLoading}
      testId={testId}
    >
      {currentAssessment && (
        <>
          {/* Out of the flow on purpose: the artwork rises out of the card's own
              bottom-left corner, under the content, and nothing above it has to
              leave a hole where it stands. Below 480px there is no width left
              beside it for the card to say anything, so it moves to the middle
              of the card's foot and the content does step out of its way. */}
          <div className={styles['decoration']} aria-hidden="true">
            <img className={styles['decoration__image']} src={image} alt="" />
          </div>
          <div className={styles['content']}>
            <AssessmentSummary
              testId={`${testId}_summary`}
              heading={`Simulacro esencial de ${subject}`}
              name={currentAssessment.name}
              timeLimit={currentAssessment.timeLimit}
              questionCount={currentAssessment.questions.length}
            />
            <AssessmentMark
              testId={`${testId}_mark`}
              score={ownGrade}
              onStart={() => handleStart(currentAssessment)}
            />
            {/* Always on the card, empty until the first mark: the board is where
                the simulacro is read from, and a card that grows one the moment
                somebody finishes reads as a different card each week. */}
            {metricsResponse && (
              <AssessmentScoreboard
                className={styles['scoreboard']}
                testId={`${testId}_scoreboard`}
                metrics={metricsResponse}
                ownUsername={profile.username}
                cutoffScore={currentAssessment.cutoffScore}
              />
            )}
          </div>
        </>
      )}
      <ResourceDialog open={previewAssessmentId !== undefined}>
        {previewAssessmentId && (
          <MultipleChoiceAssessmentPreview
            subjectId={subjectId}
            assessmentId={previewAssessmentId}
            onClose={() => setPreviewAssessmentId(undefined)}
          >
            {/* The preview is closed on the way into the simulacro, so that closing
                the simulacro lands back on the card rather than on the preview it
                was started from. */}
            <IconButton
              icon={<IoArrowForwardCircleOutline />}
              onClick={() => {
                setSelectedAssessmentId(previewAssessmentId);
                setPreviewAssessmentId(undefined);
              }}
            />
          </MultipleChoiceAssessmentPreview>
        )}
      </ResourceDialog>
      <MultipleChoiceAssessmentAttemptDialog
        subjectId={subjectId}
        assessmentId={selectedAssessmentId}
        onClose={() => setSelectedAssessmentId(undefined)}
      />
    </Card>
  );
}

export { FormativeQuestionnaireCard };
